import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import Reveal from "./Reveal";

const CITY_IMG =
  "https://images.unsplash.com/photo-1666969565832-b55bf42a900d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHxhdXN0aW4lMjB0ZXhhcyUyMGNpdHlzY2FwZXxlbnwwfHx8fDE3ODU3NzM2OTN8MA&ixlib=rb-4.1.0&q=85";

const chapters = [
  {
    n: "01",
    title: "More than two decades of comfort.",
    body: "Million-Air A/C and Heating has served Central Texas for more than twenty years. That longevity is not an accident. It is the result of showing up, doing the job properly, and treating every home and business as if it were our own. Experience like this shows in the details: cleaner installs, smarter diagnoses, and fewer surprises.",
  },
  {
    n: "02",
    title: "An A+ reputation, earned since 2005.",
    body: "We hold an A+ rating from the Better Business Bureau and have been BBB accredited since 2005. Ratings like that are built slowly, one satisfied customer at a time, and they reflect a simple promise: honest pricing, quality work, and follow-through. We would rather earn your trust than chase a quick sale.",
  },
  {
    n: "03",
    title: "An American Standard Dealer partnership.",
    body: "As an American Standard Dealer, we bring premium, high-efficiency equipment to every project, backed by manufacturer support and our own expert installation. The result is comfort that lasts and systems that run the way they were engineered to, quietly and reliably, for years to come.",
  },
  {
    n: "04",
    title: "Local, and relationship first.",
    body: "We are your neighbors. We answer honestly, we recommend only what you truly need, and we build relationships that outlast any single service call. When you call Million-Air, you are not a ticket number. You are a member of a community we are proud to serve.",
  },
];

export default function WhyUs() {
  return (
    <section id="why" className="relative bg-[#101317] grain py-24 md:py-32 overflow-hidden">
      <div className="pointer-events-none absolute -left-40 top-40 w-[420px] h-[420px] rounded-full bg-[#C9A44C]/8 blur-[120px]" />
      <div className="relative z-10 max-w-[1200px] mx-auto px-5 lg:px-10">
        <Reveal>
          <div className="flex items-center gap-3 mb-4">
            <img src="/markonlyvector.png" alt="" className="h-6 w-auto opacity-80" />
            <span className="text-xs tracking-[0.25em] uppercase text-[#9AA3AF]">Why Million-Air</span>
          </div>
          <h2 className="text-3xl md:text-5xl text-[#C9A44C] max-w-3xl">
            Trust is the product. Comfort is how we deliver it.
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.06}>
              <div className="group">
                <div className="flex items-baseline gap-4">
                  <span className="text-5xl md:text-6xl text-[#C9A44C]/30 group-hover:text-[#C9A44C]/60 transition-colors duration-500 leading-none">
                    {c.n}
                  </span>
                  <h3 className="text-2xl md:text-3xl text-white">{c.title}</h3>
                </div>
                <div className="hairline my-5" />
                <p className="text-[#9AA3AF] text-lg leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Service area statement */}
        <Reveal delay={0.1}>
          <div className="mt-20 relative overflow-hidden rounded-3xl border border-[#9AA3AF]/15">
            <img src={CITY_IMG} alt="Austin-Round Rock area" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#101317] via-[#101317]/85 to-[#101317]/60" />
            <div className="relative z-10 p-8 md:p-14 max-w-2xl">
              <div className="inline-flex items-center gap-2 text-[#C9A44C] mb-4">
                <MapPin size={20} />
                <span className="text-xs tracking-[0.25em] uppercase">Service Area</span>
              </div>
              <h3 className="text-2xl md:text-4xl text-white leading-snug">
                Based in Round Rock, serving a 50-mile radius.
              </h3>
              <p className="mt-4 text-white/85 text-lg leading-relaxed">
                We proudly cover Round Rock and the greater Austin-Round Rock-San Marcos metro area
                within a 50-mile radius. Close enough to be there quickly, local enough to genuinely
                care about the outcome.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
